import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { Receipt } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

export interface ReceiptStatsFile {
  id: string;
  filename: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  transactionId: string | null;
  hasThumbnail: boolean;
}

export interface ReceiptStats {
  count: number;
  totalSize: number;
  files: ReceiptStatsFile[];
}

export interface SanitizationWarning {
  message: string;
  items_filtered: number;
  suggestion: string;
}

export interface ReceiptsState {
  receipts: Receipt[];
  selectedReceipt: Receipt | null;
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  uploading: boolean;
  uploadProgress: number;
  stats: ReceiptStats | null;
  /** T232-PY-FE-1 — set when the receipt scanner returns 422 + proxy_mapped:
   *  true (BE-1 sanitization contract). Consumed by ReceiptScannerDialog to
   *  render the manual-edit UX for cleaned items. Cleared on reset/dismiss. */
  sanitizationWarning: SanitizationWarning | null;
}

const initialState: ReceiptsState = {
  receipts: [],
  selectedReceipt: null,
  loading: false,
  error: null,
  total: 0,
  page: 1,
  totalPages: 1,
  uploading: false,
  uploadProgress: 0,
  stats: null,
  sanitizationWarning: null,
};

export interface FetchReceiptsParams {
  dateFrom?: string;
  dateTo?: string;
  merchant?: string;
  category?: string;
  transactionId?: string;
  tags?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

export const fetchReceipts = createAsyncThunk(
  'receipts/fetchReceipts',
  async (params: FetchReceiptsParams | undefined, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.receipts.list, { params });
      return response.data as {
        data: Receipt[];
        total: number;
        page: number;
        totalPages: number;
      };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch receipts'));
    }
  }
);

export const fetchReceipt = createAsyncThunk(
  'receipts/fetchReceipt',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.receipts.get(id));
      return (response.data.data || response.data) as Receipt;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch receipt'));
    }
  }
);

export const uploadReceipt = createAsyncThunk(
  'receipts/uploadReceipt',
  async (
    { formData, onProgress }: { formData: FormData; onProgress?: (pct: number) => void },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(endpoints.receipts.upload, formData, {
        timeout: 60000,
        onUploadProgress: (e) => {
          if (e.total && onProgress) {
            onProgress(Math.round((e.loaded * 100) / e.total));
          }
        },
      });
      return response.data as { data: Receipt; suggestion?: { category: string; confidence: string; reason: string } };
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to upload receipt'));
    }
  }
);

export const updateReceipt = createAsyncThunk(
  'receipts/updateReceipt',
  async (
    { id, data }: { id: string; data: { notes?: string; tags?: string[]; metadata?: Partial<Receipt['metadata']> } },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.patch(endpoints.receipts.update(id), data);
      return (response.data.data || response.data) as Receipt;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update receipt'));
    }
  }
);

export const deleteReceipt = createAsyncThunk(
  'receipts/deleteReceipt',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.receipts.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete receipt'));
    }
  }
);

export const linkReceipt = createAsyncThunk(
  'receipts/linkReceipt',
  async ({ id, transactionId }: { id: string; transactionId: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.receipts.link(id), { transactionId });
      return (response.data.data || response.data) as Receipt;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to link receipt'));
    }
  }
);

export const unlinkReceipt = createAsyncThunk(
  'receipts/unlinkReceipt',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.receipts.unlink(id));
      return (response.data.data || response.data) as Receipt;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to unlink receipt'));
    }
  }
);

export const fetchReceiptStats = createAsyncThunk(
  'receipts/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.receipts.stats);
      return (response.data.data || response.data) as ReceiptStats;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch receipt stats'));
    }
  }
);

const receiptsSlice = createSlice({
  name: 'receipts',
  initialState,
  reducers: {
    setSelectedReceipt(state, action) {
      state.selectedReceipt = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    setUploadProgress(state, action) {
      state.uploadProgress = action.payload;
    },
    /** T232-PY-FE-1 — set when scanner returns 422 + proxy_mapped contract. */
    setSanitizationWarning(state, action) {
      state.sanitizationWarning = action.payload;
    },
    /** T232-PY-FE-1 — clear when user dismisses, re-scans, or completes save. */
    clearSanitizationWarning(state) {
      state.sanitizationWarning = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchReceipts
      .addCase(fetchReceipts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReceipts.fulfilled, (state, action) => {
        state.loading = false;
        state.receipts = action.payload.data;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchReceipts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchReceipt
      .addCase(fetchReceipt.fulfilled, (state, action) => {
        state.selectedReceipt = action.payload;
      })
      // uploadReceipt
      .addCase(uploadReceipt.pending, (state) => {
        state.uploading = true;
        state.uploadProgress = 0;
      })
      .addCase(uploadReceipt.fulfilled, (state, action) => {
        state.uploading = false;
        state.uploadProgress = 100;
        state.receipts = [action.payload.data, ...state.receipts];
        state.total += 1;
      })
      .addCase(uploadReceipt.rejected, (state, action) => {
        state.uploading = false;
        state.uploadProgress = 0;
        state.error = action.payload as string;
      })
      // updateReceipt
      .addCase(updateReceipt.fulfilled, (state, action) => {
        const idx = state.receipts.findIndex((r) => r._id === action.payload._id);
        if (idx >= 0) state.receipts[idx] = action.payload;
        if (state.selectedReceipt?._id === action.payload._id) {
          state.selectedReceipt = action.payload;
        }
      })
      // deleteReceipt
      .addCase(deleteReceipt.fulfilled, (state, action) => {
        state.receipts = state.receipts.filter((r) => r._id !== action.payload);
        state.total = Math.max(0, state.total - 1);
        if (state.selectedReceipt?._id === action.payload) {
          state.selectedReceipt = null;
        }
      })
      // linkReceipt
      .addCase(linkReceipt.fulfilled, (state, action) => {
        const idx = state.receipts.findIndex((r) => r._id === action.payload._id);
        if (idx >= 0) state.receipts[idx] = action.payload;
        if (state.selectedReceipt?._id === action.payload._id) {
          state.selectedReceipt = action.payload;
        }
      })
      // unlinkReceipt
      .addCase(unlinkReceipt.fulfilled, (state, action) => {
        const idx = state.receipts.findIndex((r) => r._id === action.payload._id);
        if (idx >= 0) state.receipts[idx] = action.payload;
        if (state.selectedReceipt?._id === action.payload._id) {
          state.selectedReceipt = action.payload;
        }
      })
      // fetchReceiptStats
      .addCase(fetchReceiptStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const {
  setSelectedReceipt,
  clearError,
  setUploadProgress,
  setSanitizationWarning,
  clearSanitizationWarning,
} = receiptsSlice.actions;
export default receiptsSlice.reducer;
